'use client'

import { Link } from '@tanstack/react-router'
import { usePathname } from '@/hooks/usePathname'
import { useState, useEffect } from 'react'
import { useTranslations } from '@/hooks/useTranslations'
import { useAuth } from './AuthProvider'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu'
import {
  LogOut,
  ChevronDown,
  Menu,
  MessageSquareWarning,
  Trash2,
  X
} from 'lucide-react'
import { DeleteAccountDialog } from './DeleteAccountDialog'
import type { User } from '@supabase/supabase-js'
import { ROUTES } from '@/config/routes'
import { getNavSections, MANUAL_HREF, type NavItem } from '@/lib/nav-items'
import { isNative } from '@/lib/platform'
import { hardNavigate } from '@/lib/hard-nav'
import { BackgroundSwitcherInline } from './BackgroundSwitcher'
import { LanguageSelector } from './LanguageSelector'
import { FeedbackDialog } from './FeedbackDialog'
import { SmartAppBanner } from './SmartAppBanner'

// Full-loading out of the SPA on native destroys the WebView document while its
// imki.sqlite connection is still open mid-sync; the next document then collides
// on the still-locked connection → "database is locked (code 5)". hardNavigate
// releases the native connection first. Web full-loads safely (no shared native
// SQLite handle), so there we let the plain <a> proceed.
function openManual(e: React.MouseEvent): void {
  if (!isNative()) return
  e.preventDefault()
  void hardNavigate(MANUAL_HREF)
}

interface NavbarProps {
  user: User
  userRole?: string | null
}

export function Navbar({ user, userRole }: NavbarProps) {
  const t = useTranslations('nav')
  const tCommon = useTranslations('common')
  const tNavbar = useTranslations('navbar')
  const tFeedback = useTranslations('feedbackDialog')
  const { signOut } = useAuth()
  const pathname = usePathname()
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [feedbackOpen, setFeedbackOpen] = useState(false)
  const [deleteAccountOpen, setDeleteAccountOpen] = useState(false)

  // Translate role label
  const getRoleLabel = (role: string | null | undefined) => {
    if (!role) return tCommon('roles.USER')
    return tCommon(`roles.${role}` as Parameters<typeof tCommon>[0]) || role
  }

  // The pre-2026-06 navbar let users reorder items (persisted under this key).
  // The order is now fixed per client feedback — drop the stale key.
  useEffect(() => {
    localStorage.removeItem('navbar-order')
  }, [])

  const isActive = (path: string) => pathname === path

  const { main, admin } = getNavSections(userRole)
  const label = (item: NavItem) => t(item.labelKey)

  // Desktop: first 4 main items inline, the rest behind "Mehr". `main` has at
  // least 5 items for every role (unit-tested), so "Mehr" is never empty and
  // the manual never needs an inline slot — but NavLink handles `external`
  // anyway so a future reshuffle can't silently break the hard-load contract.
  const visibleItems = main.slice(0, 4)
  const moreItems = main.slice(4)

  return (
    <nav className="paper-nav">
      <div className="paper-nav-container">
        <div className="paper-nav-content">
          {/* Logo & Home Link - Paper Brand */}
          <Link
            // eslint-disable-next-line @typescript-eslint/no-explicit-any -- ROUTES href isn't statically known to TanStack Router
            to={ROUTES.DASHBOARD as any}
            className="paper-brand"
          >
            <img
              src="/zerosnag-blue.png"
              alt="ZeroSnag"
              width={70}
              height={20}
              className="h-5 w-auto"
            />
          </Link>

          {/* Desktop Navigation - Hidden on Mobile */}
          <div className="hidden lg:flex paper-nav-items">
            {visibleItems.map((item) => (
              <NavLink
                key={item.href}
                href={item.href}
                icon={<item.icon className="w-4 h-4" />}
                active={isActive(item.href)}
                external={item.external}
                onNavigate={item.external ? openManual : undefined}
              >
                {label(item)}
              </NavLink>
            ))}

            {/* More dropdown — always rendered: `main` always has 5+ items,
                so at minimum Statistiken/Tipps & Hilfe live here. */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="paper-nav-dropdown-trigger">
                  <span>{tCommon('more')}</span>
                  <ChevronDown className="paper-nav-dropdown-icon" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="paper-dropdown-menu">
                {moreItems.map((item) => (
                  <DropdownMenuItem key={item.href} asChild>
                    <DropdownNavLink
                      href={item.href}
                      icon={<item.icon className="w-4 h-4" />}
                      active={isActive(item.href)}
                      external={item.external}
                      onNavigate={item.external ? openManual : undefined}
                    >
                      {label(item)}
                    </DropdownNavLink>
                  </DropdownMenuItem>
                ))}
                {/* Abrechnung/Admin — visually set apart (client feedback) */}
                {admin.length > 0 && <DropdownMenuSeparator />}
                {admin.map((item) => (
                  <DropdownMenuItem key={item.href} asChild>
                    <DropdownNavLink
                      href={item.href}
                      icon={<item.icon className="w-4 h-4" />}
                      active={isActive(item.href)}
                    >
                      {label(item)}
                    </DropdownNavLink>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {/* Right side - User menu and mobile menu button */}
          <div className="flex items-center gap-2">
            {/* Language Selector - Desktop */}
            <div className="hidden lg:block">
              <LanguageSelector />
            </div>

            {/* User Menu - Desktop */}
            <div className="hidden sm:block">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="paper-user-menu-trigger">
                    <div className="paper-avatar paper-avatar-primary">
                      {user?.email?.[0]?.toUpperCase() || 'U'}
                    </div>
                    <div className="paper-user-info">
                      <div className="paper-user-email">{user?.email}</div>
                    </div>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="paper-dropdown-menu paper-user-dropdown">
                  <div className="paper-dropdown-header">
                    <p className="paper-dropdown-user-email">{user?.email}</p>
                    <p className="paper-dropdown-user-role">{getRoleLabel(userRole)}</p>
                  </div>
                  <div className="paper-dropdown-divider" />
                  <div className="px-3 py-2">
                    <BackgroundSwitcherInline />
                  </div>
                  <div className="paper-dropdown-divider" />
                  <DropdownMenuItem onClick={() => setFeedbackOpen(true)} className="paper-dropdown-item">
                    <MessageSquareWarning className="paper-dropdown-item-icon" />
                    {tFeedback('menuItem')}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={signOut} className="paper-dropdown-item paper-dropdown-item-danger">
                    <LogOut className="paper-dropdown-item-icon" />
                    {tCommon('logout')}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => setDeleteAccountOpen(true)} className="paper-dropdown-item paper-dropdown-item-danger">
                    <Trash2 className="paper-dropdown-item-icon" />
                    Konto löschen
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden paper-mobile-menu-button"
            >
              {mobileMenuOpen ? (
                <X className="w-6 h-6" />
              ) : (
                <Menu className="w-6 h-6" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Menu Overlay */}
        {mobileMenuOpen && (
          <div className="lg:hidden paper-mobile-menu">
            <div className="paper-mobile-menu-content">
              {/* Mobile Navigation Items */}
              <div className="paper-mobile-nav-section">
                <h3 className="paper-mobile-section-title">{tNavbar('navigation')}</h3>
                <div className="paper-mobile-nav-items">
                  {main.map((item) => (
                    <MobileNavLink
                      key={item.href}
                      href={item.href}
                      icon={<item.icon className="w-5 h-5" />}
                      active={isActive(item.href)}
                      external={item.external}
                      onNavigate={(e) => {
                        setMobileMenuOpen(false)
                        if (item.external) openManual(e)
                      }}
                    >
                      {label(item)}
                    </MobileNavLink>
                  ))}
                  {admin.length > 0 && (
                    <>
                      {/* Abrechnung/Admin — visually set apart (client feedback) */}
                      <div className="my-2 border-t border-gray-200" aria-hidden="true" />
                      {admin.map((item) => (
                        <MobileNavLink
                          key={item.href}
                          href={item.href}
                          icon={<item.icon className="w-5 h-5" />}
                          active={isActive(item.href)}
                          onNavigate={() => setMobileMenuOpen(false)}
                        >
                          {label(item)}
                        </MobileNavLink>
                      ))}
                    </>
                  )}
                </div>
              </div>

              {/* Mobile User Section */}
              <div className="paper-mobile-user-section">
                <div className="paper-mobile-user-info">
                  <div className="paper-avatar paper-avatar-primary">
                    {user?.email?.[0]?.toUpperCase() || 'U'}
                  </div>
                  <div>
                    <div className="paper-mobile-user-email">{user?.email}</div>
                    <div className="paper-mobile-user-role">{getRoleLabel(userRole)}</div>
                  </div>
                </div>

                {/* Language Selector - Mobile */}
                <div className="py-3 border-t border-gray-200">
                  <LanguageSelector />
                </div>

                <button
                  onClick={() => {
                    setMobileMenuOpen(false)
                    setFeedbackOpen(true)
                  }}
                  className="paper-mobile-nav-link w-full"
                >
                  <MessageSquareWarning className="w-5 h-5" />
                  <span>{tFeedback('menuItem')}</span>
                </button>
                <button
                  onClick={() => {
                    setMobileMenuOpen(false)
                    signOut()
                  }}
                  className="paper-mobile-logout-button"
                >
                  <LogOut className="w-5 h-5" />
                  <span>{tCommon('logout')}</span>
                </button>
                <button
                  onClick={() => {
                    setMobileMenuOpen(false)
                    setDeleteAccountOpen(true)
                  }}
                  className="paper-mobile-nav-link w-full text-red-600"
                >
                  <Trash2 className="w-5 h-5" />
                  <span>Konto löschen</span>
                </button>
              </div>
            </div>
          </div>
        )}

      </div>

      {/* Under the nav bar, inside the sticky .paper-nav element so it stays
          visible on every page and while scrolling (self-gates to mobile
          browsers in app-banner.ts). */}
      <SmartAppBanner />

      <FeedbackDialog open={feedbackOpen} onOpenChange={setFeedbackOpen} />
      <DeleteAccountDialog open={deleteAccountOpen} onClose={() => setDeleteAccountOpen(false)} />
    </nav>
  )
}

interface NavLinkProps {
  href: string
  icon?: React.ReactNode
  active?: boolean
  children: React.ReactNode
  onNavigate?: (e: React.MouseEvent) => void
  // `/anleitung` is static HTML outside the TanStack route tree — it must
  // full-load via a plain <a>. Real app routes use client-side <Link>.
  external?: boolean
}

function NavLink({ href, icon, active, children, onNavigate, external }: NavLinkProps) {
  // TanStack <Link> for client-side navigation. A plain <a> here reloads the
  // whole WebView on every tap, which on native respawns PowerSyncDatabase and
  // collides with the prior document's still-open native SQLite connection →
  // "database is locked (code 5)". Client nav keeps one document, one instance.
  const className = `paper-nav-link ${active ? 'paper-nav-link-active' : ''}`
  const inner = (
    <>
      <span className="paper-nav-link-icon">{icon}</span>
      <span className="paper-nav-link-text">{children}</span>
    </>
  )
  if (external) {
    return <a href={href} onClick={onNavigate} className={className}>{inner}</a>
  }
  return (
    <Link
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- nav hrefs are dynamic ROUTES strings, not statically known to TanStack Router
      to={href as any}
      className={className}
    >
      {inner}
    </Link>
  )
}

interface MobileNavLinkProps {
  href: string
  icon?: React.ReactNode
  active?: boolean
  children: React.ReactNode
  onNavigate: (e: React.MouseEvent) => void
  // `/anleitung` is static HTML outside the TanStack route tree — it must
  // full-load via a plain <a>. Real app routes use client-side <Link>.
  external?: boolean
}

function MobileNavLink({ href, icon, active, children, onNavigate, external }: MobileNavLinkProps) {
  const className = `paper-mobile-nav-link ${active ? 'paper-mobile-nav-link-active' : ''}`
  const inner = (
    <>
      <span className="paper-mobile-nav-icon">{icon}</span>
      <span className="paper-mobile-nav-text">{children}</span>
    </>
  )
  if (external) {
    return <a href={href} onClick={onNavigate} className={className}>{inner}</a>
  }
  return (
    <Link
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- nav hrefs are dynamic ROUTES strings, not statically known to TanStack Router
      to={href as any}
      onClick={onNavigate}
      className={className}
    >
      {inner}
    </Link>
  )
}

interface DropdownNavLinkProps {
  href: string
  icon?: React.ReactNode
  active?: boolean
  children: React.ReactNode
  onNavigate?: (e: React.MouseEvent) => void
  // `/anleitung` is static HTML outside the TanStack route tree — it must
  // full-load via a plain <a>. Real app routes use client-side <Link>.
  external?: boolean
}

function DropdownNavLink({ href, icon, active, children, onNavigate, external }: DropdownNavLinkProps) {
  const className = `paper-dropdown-item ${active ? 'paper-dropdown-item-active' : ''}`
  if (external) {
    return (
      <a href={href} onClick={onNavigate} className={className}>
        {icon}
        <span className="paper-dropdown-item-text">{children}</span>
      </a>
    )
  }
  return (
    <Link
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- nav hrefs are dynamic ROUTES strings, not statically known to TanStack Router
      to={href as any}
      onClick={onNavigate}
      className={className}
    >
      {icon}
      <span className="paper-dropdown-item-text">{children}</span>
    </Link>
  )
}
